const BASE_DOMAIN = Object.freeze({
  PRODUCTION: 'squareup.com',
  STAGING: 'squareupstaging.com',
});

export enum Env {
  PRODUCTION = 'production',
  STAGING = 'staging',
  DEVELOPMENT = 'development',
  TEST = 'test',
}

const SQ_ENVIRONMENT_META_TAG = "meta[name='sq:environment']";

// this interface maps to the backend environment metadata definitions in
// app/helpers/sq/web/application_helper.rb and whatever comes back from
// public web styles `sq_environment_config` in the layout
interface EnvironmentMetadata {
  env: string;
  country_code: string;
  lang_code: string;
  locale: string;
  country_default_language: string;
  default_country: string;
  default_language: string;
  supported_countries: string[];
  detected_country_code: string | null;
  available_languages: string[];
  currency_code: string;
  internal: boolean;
  revision: string | null;
  app_subdomain: string;
}

/**
 * Returns the current environment metadata from the meta tag, or null when
 * the tag is absent (e.g. crawler-rendered pages like Google Storebot)
 * @returns EnvironmentMetadata | null
 */
export const getEnvironmentMetadata = (): EnvironmentMetadata | null => {
  const envMetadata = document.querySelector(SQ_ENVIRONMENT_META_TAG);

  if (!envMetadata) {
    return null;
  }

  return JSON.parse(envMetadata.getAttribute('content'));
};

/**
 * @returns Env - the current environment, defaulting to production when the
 * environment meta tag is absent (e.g. crawler-rendered pages) so callers
 * fail safe instead of crashing or falling open to dev/staging behavior.
 * Use getEnvironmentMetadata() to detect a genuinely missing tag.
 */
export const getEnvironment = (): Env => {
  const metaData = getEnvironmentMetadata();

  if (!metaData) {
    return Env.PRODUCTION;
  }

  return Env[metaData.env.toUpperCase()];
};

/**
 * if we need to get the domain for a different subdomain like `api.` for example
 * we can use this function to get the domain for the current environment
 * @param subDomainOverride - the subdomain to use instead of the app subdomain
 * @returns string - the domain for the current environment
 */
export const getDomain = (subDomainOverride?: string): string => {
  // The sq:environment meta tag can be absent on crawler-rendered pages
  // (e.g. Google Storebot). Assume production in that case rather than
  // crashing with "Cannot read properties of null (reading 'env')".
  const metadata = getEnvironmentMetadata();
  const env = metadata?.env ?? Env.PRODUCTION;
  const appSubdomain = metadata?.app_subdomain ?? 'app.';
  const host =
    env === Env.PRODUCTION ? BASE_DOMAIN.PRODUCTION : BASE_DOMAIN.STAGING;

  // other subdomains do not need the 'app' subdomain too
  const subdomain =
    subDomainOverride === undefined ? appSubdomain : subDomainOverride;

  return `${subdomain}${host}`;
};
